import { FC, MouseEvent, ReactNode, useRef, useState, useCallback, useEffect } from 'react';
import { graphql, useFragment, useRelayEnvironment } from 'react-relay';
import classnames from 'classnames';

// components
import { Carousel } from 'dibs-carousel';
import { FormattedMessage } from 'dibs-react-intl';
import { SeoLink } from 'dibs-elements/exports/SeoLink';
import {
    SharedRespRelatedItem,
    X_SMALL_TILE_TYPE,
} from '../../../shared/SharedRespRelatedItem/SharedRespRelatedItem';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';
import {
    QuickViewProvider,
    QuickViewProviderChildrenProps,
} from '../../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../../components/global/FavoriteIcons/FavoritesProvider';
import { FavoritesProviderChildrenProps } from '../../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';

import { useClientState } from 'dibs-react-hooks/exports/useClientState';

import { PdpRespProductTileCarousel_viewer$key } from './__generated__/PdpRespProductTileCarousel_viewer.graphql';
import { PdpRespProductTileCarousel_user$key } from './__generated__/PdpRespProductTileCarousel_user.graphql';
import {
    PdpRespProductTileCarousel_items$key,
    PdpRespProductTileCarousel_items$data,
} from './__generated__/PdpRespProductTileCarousel_items.graphql';

// styles
import styles from './PdpRespProductTileCarousel.scss';

type CarouselItem = PdpRespProductTileCarousel_items$data[number];

export type PdpRespProductTileCarouselProps = {
    viewer: PdpRespProductTileCarousel_viewer$key;
    items: PdpRespProductTileCarousel_items$key;
    user: PdpRespProductTileCarousel_user$key | null;
    currency: string;
    title: ReactNode;
    location: string;
    viewAllLinkData?: {
        path: string | null;
        isLinkable: boolean | null;
    } | null;
    showArrows?: boolean;
    dataTn?: string;
    onCarouselRendered?: () => void;
    onCarouselVisible?: () => void;
    onClickArrow?: (direction: string) => void;
    onClickItemTile?: (item: CarouselItem, index: number, e: MouseEvent) => void;
    onClickViewAll?: (e: MouseEvent) => void;
    onItemsVisible?: (items: CarouselItem[], startIndex: number) => void;
    onCarouselReset?: () => void;
};

type RenderItem = FC<{
    index: number;
    quickView: QuickViewProviderChildrenProps;
    favorites: FavoritesProviderChildrenProps;
}>;

const ITEMS_PER_PAGE = 5;

const viewerFragment = graphql`
    fragment PdpRespProductTileCarousel_viewer on Viewer {
        ...SharedRespRelatedItem_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespProductTileCarousel_user on User {
        serviceId
        ...SharedRespRelatedItem_user
    }
`;

const itemsFragment = graphql`
    fragment PdpRespProductTileCarousel_items on Item
    @relay(plural: true)
    @argumentDefinitions(userCountryCode: { type: "String" }, priceBookName: { type: "String" }) {
        ecommerceTrackingParams
        categoryPath
        categoryCode
        serviceId
        browseUrl
        title
        ...SharedRespRelatedItem_item
            @arguments(userCountryCode: $userCountryCode, priceBookName: $priceBookName)
    }
`;

export const PdpRespProductTileCarousel: FC<PdpRespProductTileCarouselProps> = ({
    viewer: viewerRef,
    items: itemsRef,
    user: userRef,
    currency,
    title,
    location,
    viewAllLinkData,
    showArrows,
    dataTn,
    onCarouselRendered,
    onCarouselVisible,
    onClickArrow,
    onClickItemTile = () => {},
    onClickViewAll = () => {},
    onItemsVisible = () => {},
    onCarouselReset = () => {},
}) => {
    const viewer = useFragment(viewerFragment, viewerRef);

    const items = useFragment(itemsFragment, itemsRef);
    const user = useFragment(userFragment, userRef);
    const environment = useRelayEnvironment();

    const carouselRef = useRef(null);
    const impressionTrackingRef = useRef<Record<string, boolean | undefined>>({});

    const [carouselPage, setCarouselPage] = useState(0);
    const [carouselIndex, setCarouselIndex] = useState(0);
    const [isCarouselVisible, setIsCarouselVisible] = useState(false);

    const isClient = useClientState();

    const userId = user?.serviceId;
    const hasItems = items.length;

    const handleClickArrow = (direction: string): void => {
        if (onClickArrow) {
            onClickArrow(direction);
        }
    };

    const arrowsHidden = !(showArrows || onClickArrow);

    const renderItem = useCallback<RenderItem>(
        ({ index, quickView, favorites }) => {
            const similarItem = items[index];

            if (!similarItem) {
                return null;
            }

            return (
                <SharedRespRelatedItem
                    showAuctionBids
                    hideTileImagePadding
                    user={user}
                    index={index}
                    viewer={viewer}
                    item={similarItem}
                    currency={currency}
                    quickView={quickView}
                    showMeasurements={false}
                    onClick={e => onClickItemTile(similarItem, index, e)}
                    favorites={favorites}
                    tileType={X_SMALL_TILE_TYPE}
                />
            );
        },
        [items, user, viewer, currency, onClickItemTile]
    );

    useEffect(() => {
        if (hasItems && onCarouselRendered) {
            onCarouselRendered();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isCarouselVisible && onCarouselVisible) {
            onCarouselVisible();
        }
    }, [isCarouselVisible, onCarouselVisible]);

    useEffect(() => {
        if (!isCarouselVisible || !onItemsVisible) {
            return;
        }

        const startIndex = carouselPage * ITEMS_PER_PAGE;
        const visibleItems: CarouselItem[] = [];

        items.slice(startIndex, startIndex + ITEMS_PER_PAGE).forEach(item => {
            const id = item.ecommerceTrackingParams?.id;
            if (!impressionTrackingRef.current[id]) {
                visibleItems.push(item);
                impressionTrackingRef.current[id] = true;
            }
            onItemsVisible(visibleItems, startIndex);
        });
    }, [carouselPage, items, isCarouselVisible, onItemsVisible]);

    useEffect(() => {
        // if user logs in after paginating pdp refetch container resets this pagination container
        // in this case reset carousel
        if (userId) {
            setCarouselIndex(0);
            onCarouselReset();
        }
    }, [userId, onCarouselReset]);

    if (!hasItems) {
        return null;
    }

    return (
        <div className={styles.container} ref={carouselRef} data-tn="pdp-dealer-grid">
            <span className={styles.title}>{title}</span>
            {viewAllLinkData && (
                <SeoLink
                    className={styles.viewAllLink}
                    dataTn="pdp-product-tile-carousel-view-all-link"
                    linkData={viewAllLinkData}
                    onClick={onClickViewAll}
                >
                    <FormattedMessage
                        defaultMessage="View All"
                        id="PdpRespProductTileCarousel.viewAll"
                    />
                </SeoLink>
            )}
            <VisibilityTracker
                elementRef={carouselRef}
                onVisibilityChange={({ isVisible }) => setIsCarouselVisible(isVisible)}
            />
            <FavoritesProvider
                location={location}
                disable={!isClient}
                userId={userId || null}
                environment={environment}
                itemIds={items.map(node => node.serviceId)}
            >
                {({ props: favorites }: { props: FavoritesProviderChildrenProps }) => (
                    <QuickViewProvider>
                        {({ quickView }) => (
                            <Carousel
                                isInfinite
                                slideCarouselItems
                                step={ITEMS_PER_PAGE}
                                index={carouselIndex}
                                itemsPerPage={ITEMS_PER_PAGE}
                                totalItems={items.length}
                                onLeftArrowClick={() => handleClickArrow('left')}
                                onRightArrowClick={() => handleClickArrow('right')}
                                onPageChange={({ page }: { page: number }) => setCarouselPage(page)}
                                onIndexChange={({ index }: { index: number }) =>
                                    setCarouselIndex(index)
                                }
                                renderItem={({ index }: { index: number }) =>
                                    renderItem({ index, quickView, favorites })
                                }
                                classNames={{
                                    wrapper: classnames({
                                        [styles.arrowsHidden]: arrowsHidden,
                                    }),
                                    item: styles.item,
                                    arrowLeft: classnames(styles.arrow, styles.arrowLeft),
                                    arrowRight: classnames(styles.arrow, styles.arrowRight),
                                    list: styles.list,
                                }}
                                dataTn={dataTn}
                            />
                        )}
                    </QuickViewProvider>
                )}
            </FavoritesProvider>
        </div>
    );
};
